import { captureException } from "@/services/sentry";
import type { ApiErrorResponse } from "@vardic/shared/error-codes";
import { getFrontendFeatureFlags } from "@vardic/shared/utils/feature-flag-context";
import { ApiError, normalizeApiErrorResponse } from "./errors";
import { API_BASE_URL } from "./config";
import { DEBUG_AUTH, getHeaders } from "./auth";
import {
  getCsrfToken,
  hasCsrfCookie,
  isCsrfProtectedMethod,
} from "./csrf";
import { getActiveTeamId } from "@/services/teams/active-team";

export { API_BASE_URL };

// JSON-serializable object type for request bodies
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonSerializable = Record<string, any>;

export type RequestOptions = {
  method?: string;
  body?: BodyInit | JsonSerializable;
  auth?: boolean;
  contentType?: "json" | "none";
  headers?: HeadersInit;
};

async function buildRequestInit(options: RequestOptions): Promise<RequestInit> {
  const {
    method = "GET",
    body,
    auth = true,
    contentType = "none",
    headers,
  } = options;

  let baseHeaders: HeadersInit = {};
  if (auth) {
    const shouldUseBearer = !hasCsrfCookie();
    if (shouldUseBearer) {
      try {
        baseHeaders = await getHeaders(contentType === "json");
      } catch (error) {
        // Allow cookie-session auth to proceed when no bearer token is available.
        if (!(error instanceof ApiError) || error.code !== "AUTH_NO_TOKEN") {
          throw error;
        }
      }
    }
  }

  const finalHeaders = new Headers(baseHeaders);

  if (contentType === "json" && !finalHeaders.has("Content-Type")) {
    finalHeaders.set("Content-Type", "application/json");
  }

  if (headers) {
    const extra = new Headers(headers);
    extra.forEach((value, key) => {
      finalHeaders.set(key, value);
    });
  }

  if (isCsrfProtectedMethod(method) && !finalHeaders.has("x-csrf-token")) {
    // pirx-i9s6p: the token may come from the explicitly delivered
    // session-establishment response (cross-origin deployment) or from the
    // readable cookie (same-origin). `hasCsrfCookie` above already keys off
    // the same combined source, so Bearer/cookie fallback selection stays
    // consistent with which token actually gets sent.
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      finalHeaders.set("x-csrf-token", csrfToken);
    }
  }

  // Attach the active-team header on authenticated requests so writes are
  // tagged with (and reads scoped to) the selected team. The backend re-checks
  // membership (resolveActiveTeamId) and 403s if the caller does not belong —
  // this header is a hint, never a grant. A caller-supplied header wins.
  if (auth && !finalHeaders.has("x-team-id")) {
    const activeTeamId = getActiveTeamId();
    if (activeTeamId) {
      finalHeaders.set("x-team-id", activeTeamId);
    }
  }

  let requestBody: BodyInit | undefined;
  if (body !== undefined) {
    if (
      contentType === "json" &&
      !(body instanceof FormData) &&
      typeof body !== "string"
    ) {
      requestBody = JSON.stringify(body);
    } else {
      requestBody = body as BodyInit;
    }
  }

  return {
    method,
    credentials: "include",
    headers: finalHeaders,
    body: requestBody,
  };
}

export async function handleErrorResponse(
  response: Response,
  method: string,
): Promise<never> {
  let errorData: ApiErrorResponse | undefined;
  try {
    const payload: unknown = await response.json();
    errorData = normalizeApiErrorResponse(payload);
  } catch {
    errorData = undefined;
  }

  // No recognized prose in the payload (proxy 502/503/504 HTML page, a 413,
  // a non-JSON body): leave the message empty rather than build an
  // untranslated "API error: <statusText>" placeholder. Callers fall back to
  // their translated key when `err.message` is empty, and the HTTP status
  // still travels on `error.status` (e.g. 429 handling in
  // resolveApiErrorMessage).
  const message = errorData?.message || errorData?.error || "";
  const error = new ApiError(
    message,
    response.status,
    errorData,
    errorData?.code,
  );

  if (DEBUG_AUTH && errorData?.code) {
    console.warn(`API client error code: ${errorData.code}`);
  }

  try {
    const featureFlags = await getFrontendFeatureFlags();
    captureException(error, {
      contexts: {
        apiError: {
          status: response.status,
          url: response.url,
          method,
          featureFlags: featureFlags.flags,
        },
      },
    });
  } catch {
    console.error("Sentry capture failed for API error");
  }

  throw error;
}

export async function handleResponse<T>(
  response: Response,
  method: string,
): Promise<T> {
  if (!response.ok) {
    await handleErrorResponse(response, method);
  }

  // A 204 (the backend's oak applies 204 No Content when a handler resolves a
  // null body, e.g. getCurrentAssessment with no current assessment) or any
  // response with no body stream has no JSON to parse: resolve `null`
  // instead of rejecting with the raw SyntaxError `response.json()` throws
  // on an empty body.
  if (response.status === 204 || response.body === null) {
    // The wire cannot distinguish "handler returned null" from "no body":
    // callers declaring a `| null` result (e.g. getCurrentAssessment) get
    // null back as their declared type says.
    return null as T;
  }

  const payload: unknown = await response.json();
  return payload as T;
}

export async function request<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const response = await fetch(
    `${API_BASE_URL}${path}`,
    await buildRequestInit(options),
  );

  return handleResponse<T>(response, options.method ?? "GET");
}

export async function requestBlob(
  path: string,
  options: RequestOptions = {},
): Promise<Blob> {
  const response = await fetch(
    `${API_BASE_URL}${path}`,
    await buildRequestInit(options),
  );

  if (!response.ok) {
    await handleErrorResponse(response, options.method ?? "GET");
  }

  return response.blob();
}

export async function requestVoid(
  path: string,
  options: RequestOptions = {},
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}${path}`,
    await buildRequestInit(options),
  );

  if (!response.ok) {
    await handleErrorResponse(response, options.method ?? "GET");
  }
}
